import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import Box from '@repo/ui/box'
import { MapIcon } from '@repo/ui/icons'
import { ImageDisplayer } from '@repo/ui/image-displayer'
import TextEditor from '@repo/ui/text-editor'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripAddress } from '../../../../../api/trip-addresses/get-trip-address'
import { getTripBookingDispatchPlace } from '../../../../../api/trip-booking-dispatch-places/get-trip-booking-dispatch-place'
import { useDataProps } from '../../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { TripAddressPartial } from '../../../../../lib/types/models/trip-address/trip-address'
import { TripBookingDispatchPlaceProperties } from '../../../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place.properties'
import { DataProps } from '../../../../../lib/types/utils'
import { ItemNotFound } from '../../../../elements/item-not-found'
import LoadingScreen from '../../../../elements/loading/loading-screen'
import { LabelValue } from '.'

export function MeetingPlaceSection(props: DataProps<TripAddressPartial>) {
  const { t } = useTranslate()
  const { data: tripAddress, isPending } = useDataProps(props, getTripAddress)

  if (isPending) return <LoadingScreen />
  if (!tripAddress) return <ItemNotFound title="Lieu de RDV non trouvé" />

  return (
    <div className={'flex flex-col gap-4'}>
      <Box className={'bg-white-hover rounded-lg p-4 border border-grey-light'}>
        {tripAddress?.adresseURI && (
          <div className={'flex flex-row gap-2 p-2 bg-primary-light rounded-sm items-start'}>
            <MapIcon className={'min-w-6 w-6 min-h-6 h-6'} />

            <Typography.Link
              variant={'tag'}
              style={{ overflowWrap: 'anywhere' }}
              className={'self-stretch flex items-center'}
              color={tailwindColors.primary.DEFAULT}
              onClick={() => window.open(tripAddress.adresseURI, '_blank')}
            >
              {tripAddress.adresseURI}
            </Typography.Link>
          </div>
        )}

        <div className={'flex flex-row gap-2 justify-between mobile:flex-col tab:flex-col mt-3'}>
          <LabelValue label={t('address.label')}>
            <Typography variant={'tag'}>{valueRender(tripAddress?.label)}</Typography>
          </LabelValue>

          <LabelValue label={t('address.address1.label')}>
            <Typography variant={'tag'}>{valueRender(tripAddress?.address1)}</Typography>
          </LabelValue>

          <LabelValue label={t('address.address2.label')}>
            <Typography variant={'tag'}>{valueRender(tripAddress?.address2)}</Typography>
          </LabelValue>

          <LabelValue label={t('address.zipCode.label')}>
            <Typography variant={'tag'}>{valueRender(tripAddress?.zipCode)}</Typography>
          </LabelValue>

          <LabelValue label={t('address.details.city')}>
            <Typography variant={'tag'}>{valueRender(tripAddress?.city)}</Typography>
          </LabelValue>
        </div>
      </Box>

      {tripAddress?.comments && <TextEditor value={tripAddress.comments} readOnly hideControls />}

      <Box className={'bg-white-hover rounded-lg p-4 border border-grey-light'}>
        <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
          {t('common.click.to.enlarge')}
        </Typography>

        <ImageDisplayer images={tripAddress?.pictures} />
      </Box>
    </div>
  )
}

type DispatchPlaceMeetingPlaceSectionProps = {
  dispatchPlaceId: number
}

export function DispatchPlaceMeetingPlaceSection({
  dispatchPlaceId
}: DispatchPlaceMeetingPlaceSectionProps) {
  const { data: place, isFetching } = useQuery({
    queryKey: ['dispatchPlace', dispatchPlaceId],
    queryFn: () => getTripBookingDispatchPlace<'PARTIAL'>(dispatchPlaceId, 'PARTIAL'),
    enabled: !!dispatchPlaceId,
    select: (dispatchPlace) =>
      TripBookingDispatchPlaceProperties.getParentsMeetingPlace(dispatchPlace)
  })

  if (isFetching) return <LoadingScreen />
  if (!place) return <ItemNotFound title="Lieu de RDV non trouvé" />

  return <MeetingPlaceSection data={place} />
}
